import type { LegendItem } from '../../features-modules/shared/table-legend-overlay/table-legend-overlay.component';

/**
 * Single registry of short table header labels (mobile + compact tables).
 * Add keys here when new screens need abbreviations; do not duplicate per feature.
 */
export const HEADER_ABBR = {
  participants: 'Participants',
  position: 'Pos',
  match_w_l: 'M W/L',
  game_w_l: 'G W/L',
  score_w_l: 'S W/L',
  points: 'PTS',
  rank: 'RNK',
  sr_no: 'Sn',
  round: 'Rd',
  match_order: 'MO',
  date: 'Dt',
  time: 'Tm',
  date_time: 'Dt/Tm',
  table: 'Tbl',
  result: 'Res',
  player: 'Player',
  team: 'Team',
  member_association: 'MA',
  club: 'C',
  set_score: 'SS',
  game_score: 'GS',
  match_score: 'MS',
  venue: 'Vn',
  view: 'Vw',
  /** Registration / listing tables */
  dob: 'DoB',
  eu_non_eu: 'Eu/NE',
  total_players: 'TP',
  pair_status: 'Ps',
  verification_status: 'Vs',
  cost: 'Ct',
  status: 'St',
  actions: 'Act',
  amount: 'Amt',
  team_name: 'Tm',
  team_and_club: 'T&C',
  player_name: 'Pn',
  /** Standings / team member drawer (mobile) */
  member: 'Mm',
  participant_type: 'PT',
  country: 'Cy',
  /** Point leaderboard (combined ranking + rating columns) */
  bonus_points: 'BP',
  elo_points: 'EL',
  total_points: 'TOT',
} as const;

export type HeaderAbbrKey = keyof typeof HEADER_ABBR;

/** Long labels for legend overlay (omit keys that should not appear in abbr→full popover). */
export const HEADER_ABBR_FULL: Readonly<
  Partial<Record<HeaderAbbrKey, string>>
> = {
  participants: 'Participants',
  position: 'Position',
  match_w_l: 'Match Win / Loss',
  game_w_l: 'Game Win / Loss',
  score_w_l: 'Score Win / Loss',
  points: 'Points',
  rank: 'Rank',
  sr_no: 'Sr. No.',
  round: 'Round',
  match_order: 'Match order',
  date: 'Date',
  time: 'Time',
  date_time: 'Date & Time',
  table: 'Table',
  result: 'Result',
  player: 'Player',
  team: 'Team',
  member_association: 'Member association',
  club: 'Club',
  set_score: 'Set score',
  game_score: 'Game score',
  match_score: 'Match score',
  venue: 'Venue',
  view: 'View',
  dob: 'Date of birth',
  eu_non_eu: 'EU / Non-EU',
  total_players: 'Total players',
  pair_status: 'Pair status',
  verification_status: 'Verification status',
  cost: 'Cost',
  status: 'Status',
  actions: 'Actions',
  amount: 'Amount',
  team_name: 'Team name',
  team_and_club: 'Team & Club',
  player_name: 'Player name',
  member: 'Member',
  participant_type: 'Participant type',
  country: 'Country',
  bonus_points: 'Bonus points',
  elo_points: 'Elo points',
  total_points: 'Total points',
};

export function headerAbbr(key: HeaderAbbrKey): string {
  return HEADER_ABBR[key];
}

export function headerAbbrFull(key: HeaderAbbrKey): string | undefined {
  return HEADER_ABBR_FULL[key];
}

/**
 * Build legend rows for visible column keys; skips keys with no full label.
 * @param keys Column keys to render, in display order.
 * @param overrides Per-key abbr/full text that bypasses the static HEADER_ABBR
 *   maps — used for keys wired to {@link TenantLabelOverrideService} (e.g.
 *   `eu_non_eu`), where the visible text is resolved via ngx-translate instead.
 */
export function legendItemsFromKeys(
  keys: HeaderAbbrKey[],
  overrides?: Partial<Record<HeaderAbbrKey, LegendItem>>
): LegendItem[] {
  return keys
    .map((k) => {
      const override = overrides?.[k];
      const full = override?.full ?? headerAbbrFull(k);
      if (full == null || full === '') {
        return null;
      }
      return { abbr: override?.abbr ?? headerAbbr(k), full };
    })
    .filter((item): item is LegendItem => item != null);
}

/** @deprecated Use HEADER_ABBR — kept for incremental migration of external imports. */
export const HeaderAbbr = HEADER_ABBR;
